import { Component, createRef, lazy, Suspense, type ReactNode } from 'react';
import { Button } from './components/Button';
import { isChunkFailure } from './components/ErrorState';

/**
 * 🔴 THE TILL LOADS ON DEMAND — design-system DS-11a0 (owner decision, 2026-10-07).
 *
 * `App.tsx` imported `TillApp` statically, so the whole till (17 files under `src/till`, about 52 KB of the `index`
 * chunk) was in every back-office first load, for people who never open it. The till is now its own chunk: the back
 * office starts faster, and a till pays one extra request the first time it opens.
 *
 * ## ⚠️ WHAT A DROPPED CONNECTION CAN AND CANNOT DO NOW
 *
 * - **While the till is open, nothing changes.** The chunk is fetched with the page, once; after that the code is in
 *   memory, as it was when it rode in the entry. The till's offline parts (the catalogue snapshot, the count queue,
 *   the device and session in `localStorage`, the price question handed to `DialogHost`) never fetch code.
 * - **The one new failure** is a connection that drops between the page and its till chunk, or a release that replaced
 *   the chunk while an old page was open. That is a chunk failure, and `TillBoundary` says so in the till's own frame
 *   with Reload, rather than a blank counter.
 * - **Opened offline**, the till behaves as before: the page itself (`index.html`, not cached immutably) must arrive
 *   first, and if it does from the browser's cache, the till chunk under `/assets/` (served `immutable`) comes from
 *   the same cache. There is no service worker yet (DS-18, O-14); when there is, it precaches this chunk with the shell.
 */
export function isTillPath(path: string): boolean {
  // DS-2a review: `/pos` and `/pos/…` are the till — but not `/post-dated-cheques`, and not the back office's own
  // register and session screens under /pos/, which a prefix test sent to the till on a refresh.
  return (path === '/pos' || path.startsWith('/pos/')) && !/^\/pos\/(registers|sessions)(\/|$)/.test(path);
}

let loading: Promise<typeof import('./till/TillApp')> | null = null;

/** The till's chunk, asked for once; a failed fetch is forgotten, so the next ask (a hover, a reload) tries again. */
export function loadTill(): Promise<typeof import('./till/TillApp')> {
  loading ??= import('./till/TillApp').catch((error: unknown) => {
    loading = null;
    throw error;
  });
  return loading;
}

/**
 * PREFETCH: a pointer over, or focus on, a same-origin link to the till starts its chunk, so a cashier rarely waits.
 * Delegated on the document, so a link drawn later counts too. Another origin is skipped: the office host sends `/pos`
 * to the workspace host, whose `/assets/` is a different cache entry. `load` is the till's loader; a spec passes its own.
 */
export function watchTillLinks(doc: Document = document, load: () => Promise<unknown> = loadTill): void {
  const prefetch = (event: Event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (link instanceof HTMLAnchorElement && link.origin === window.location.origin && isTillPath(link.pathname)) {
      load().catch(() => undefined);
    }
  };
  doc.addEventListener('pointerover', prefetch, { passive: true });
  doc.addEventListener('focusin', prefetch);
}

const TillApp = lazy(() => loadTill().then((m) => ({ default: m.TillApp })));

/**
 * The till's frame while its code arrives: the same `.wb-till` bar and body the till draws (TillApp.tsx `Frame`), so the
 * counter does not flash a different page, with one calm line rather than a spinner.
 */
export function TillOpening() {
  return (
    <div className="wb-till">
      <header className="wb-till__bar">
        <strong>Workbook till</strong>
      </header>
      <main className="wb-till__body">
        <p className="wb-till__lead" role="status">
          Opening the till…
        </p>
      </main>
    </div>
  );
}

/**
 * A till chunk that did not arrive offers Reload in the till's frame (Try again cannot work: `React.lazy` keeps the
 * failure, as the routers' RouteError knows). Any other throw is handed on to the root's crash screen (`main.tsx`).
 */
export class TillBoundary extends Component<{ children: ReactNode }, { failed: boolean; error: unknown }> {
  override state: { failed: boolean; error: unknown } = { failed: false, error: null };
  private heading = createRef<HTMLHeadingElement>();

  static getDerivedStateFromError(error: unknown): { failed: boolean; error: unknown } {
    return { failed: true, error };
  }

  override componentDidMount(): void {
    if (this.state.failed) this.heading.current?.focus();
  }

  override componentDidUpdate(_: unknown, before: { failed: boolean }): void {
    if (this.state.failed && !before.failed) this.heading.current?.focus();
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    if (!isChunkFailure(this.state.error)) throw this.state.error;
    return (
      <div className="wb-till">
        <header className="wb-till__bar">
          <strong>Workbook till</strong>
        </header>
        <main className="wb-till__body">
          <div className="wb-till__panel" role="alert">
            <h1 className="wb-till__lead" tabIndex={-1} ref={this.heading}>
              The till could not be opened
            </h1>
            <p>
              Its screens did not arrive: the connection dropped, or Workbook was updated. Nothing on this device is
              lost. Reload when the shop is back online.
            </p>
            <div className="wb-till__row">
              <Button variant="primary" onClick={() => window.location.reload()}>
                Reload
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }
}

/** The till, loaded on demand, inside its boundary, with its own opening frame. */
export function Till() {
  return (
    <TillBoundary>
      <Suspense fallback={<TillOpening />}>
        <TillApp />
      </Suspense>
    </TillBoundary>
  );
}

/**
 * Run once by `App.tsx` when it loads. On a till path, the chunk is asked for NOW, beside React's first render rather
 * than after it, and the page is pinned light (O-4). DS-11a3: `index.html`'s pre-paint script pins it first, before the
 * stylesheet paints, with this function's test (theme.spec.ts holds the two together); this line is the second pin, for
 * a page whose inline script was taken out. Anywhere else, links to the till prefetch it.
 */
export function startTill(path: string = window.location.pathname, load: () => Promise<unknown> = loadTill): boolean {
  if (!isTillPath(path)) {
    watchTillLinks(document, load);
    return false;
  }
  document.documentElement.setAttribute('data-theme', 'light');
  load().catch(() => undefined);
  return true;
}
